// RiskAssessmentPage.js
import React from 'react';
import styles from './RiskAssessmentPage.module.css';

const RiskAssessmentPage = () => {
  return (
    <div className={styles.riskAssessmentPage}>
      {/* Main Content Section */}
      <section className={styles.mainSection}>
        <div className={styles.container}>
          <div className={styles.contentGrid}>
            {/* Left Content */}
            <div className={styles.leftContent}>
              <h1 className={styles.pageTitle}>Risk Assessment Services</h1>
              <p className={styles.pageDescription}>
                At Virtual IP, our Risk Assessment Services are designed to identify, 
                evaluate, and help mitigate potential threats within your IT 
                infrastructure. With the cybersecurity landscape constantly evolving, 
                it's critical to proactively uncover vulnerabilities before they can be 
                exploited. Our detailed and tailored risk assessments empower your 
                organization to safeguard sensitive data, reduce attack surfaces, and 
                stay ahead of compliance requirements.
              </p>
            </div>

            {/* Right Visual */}
            <div className={styles.rightVisual}>
              <div className={styles.heroIllustration}>
                <div className={styles.laptopContainer}>
                  <div className={styles.laptop}>
                    <div className={styles.screen}>
                      <div className={styles.screenContent}>
                        <div className={styles.chartBars}>
                          <div className={styles.bar} style={{ height: '60%', backgroundColor: '#ff6b6b' }}></div>
                          <div className={styles.bar} style={{ height: '80%', backgroundColor: '#4ecdc4' }}></div>
                          <div className={styles.bar} style={{ height: '40%', backgroundColor: '#45b7d1' }}></div>
                          <div className={styles.bar} style={{ height: '90%', backgroundColor: '#96ceb4' }}></div>
                        </div>
                        <div className={styles.dollarIcon}>$</div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.documentStack}>
                    <div className={styles.document}></div>
                    <div className={styles.document}></div>
                    <div className={styles.document}></div>
                  </div>
                  <div className={styles.magnifyingGlass}>
                    <div className={styles.glassCircle}></div>
                    <div className={styles.glassHandle}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Holistic Risk Identification Section */}
      <section className={styles.holisticSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Holistic Risk Identification and Evaluation
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Our team at Virtual IP performs an end-to-end assessment of your 
                digital environment—including software systems, hardware 
                devices, and network architecture. We uncover risks from outdated 
                technologies, misconfigurations, insider threats, and gaps in 
                operational security. By understanding these weak points, we 
                create targeted mitigation strategies to strengthen your cyber 
                defenses.
              </p>
            </div>
            <div className={styles.rightVisual}>
            <img
              src="/riska1.png" 
              alt="Hero Visual"
              className={styles.heroImage}
            />
            </div>

          </div>
        </div>
      </section>

      {/* Gap Analysis Section */}
      <section className={styles.gapAnalysisSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionVisual}>
              <div className={styles.gapAnalysisIllustration}>
                <div className={styles.analysisBackground}>
                  <div className={styles.riskGauge}>
                    <div className={styles.gaugeArc}></div>
                    <div className={styles.gaugePointer}></div>
                    <div className={styles.riskLabel}>RISK</div>
                  </div>
                  <div className={styles.analysisPersons}>
                    <div className={styles.person1}></div>
                    <div className={styles.person2}></div>
                  </div>
                  <div className={styles.dataElements}>
                    <div className={styles.chartElement}></div>
                    <div className={styles.dollarSymbol}>$</div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Proactive Gap Analysis & Vulnerability Scanning
              </h2>
              <p className={styles.sectionDescription}>
                Using industry-leading scanning tools and techniques, Virtual IP 
                conducts thorough vulnerability scans and gap analysis of your 
                infrastructure and applications. From detecting open ports to 
                misconfigured firewalls or missing patches, we help you gain full 
                visibility into areas that may be exploited by attackers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Mitigation Section */}
      <section className={styles.mitigationSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Tailored Risk Mitigation Strategies
              </h2>
              <p className={styles.sectionDescription}>
                Once risks are identified, we don't stop there. Virtual IP delivers 
                customized, actionable solutions to close security gaps. Whether 
                it's upgrading outdated systems, enforcing secure configurations, 
                enhancing employee awareness, or adopting encryption protocols, 
                we ensure your cybersecurity framework is both practical and 
                effective.
              </p>
            </div>
            <div className={styles.sectionVisual}>
              <div className={styles.mitigationIllustration}>
                <div className={styles.whiteboard}>
                  <div className={styles.whiteboardContent}>
                    <div className={styles.riskFlow}>
                      <div className={styles.riskBubble}>RISK</div>
                      <div className={styles.arrow}></div>
                      <div className={styles.identifyBubble}>IDENTIFY</div>
                      <div className={styles.assessBubble}>ASSESS</div>
                      <div className={styles.arrow}></div>
                      <div className={styles.mitigationStamp}>RISK MITIGATION</div>
                      <div className={styles.arrow}></div>
                      <div className={styles.resultBubble}>RESULT</div>
                      <div className={styles.trackBubble}>TRACK</div>
                      <div className={styles.arrow}></div>
                      <div className={styles.reduceBubble}>REDUCE</div>
                      <div className={styles.arrow}></div>
                      <div className={styles.monitorBubble}>MONITOR</div>
                    </div>
                  </div>
                  <div className={styles.whiteboardMarker}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className={styles.complianceSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionVisual}>
              <div className={styles.complianceIllustration}>
                <div className={styles.serverStack}>
                  <div className={styles.server}></div>
                  <div className={styles.server}></div>
                  <div className={styles.server}></div>
                </div>
                <div className={styles.securityElements}>
                  <div className={styles.lockIcon}></div>
                  <div className={styles.keyIcon}></div>
                  <div className={styles.shieldIcon}></div>
                  <div className={styles.userIcon}></div>
                </div>
                <div className={styles.cloudConnection}></div>
                <div className={styles.databaseIcon}></div>
                <div className={styles.documentIcon}></div>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Compliance-Centric Approach
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Virtual IP integrates compliance readiness into every risk 
                assessment. Whether you need to align with GDPR, HIPAA, PCI-DSS, or other regulatory standards, we help ensure your 
                organization meets legal and industry-specific requirements—
                avoiding costly fines and safeguarding your reputation.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RiskAssessmentPage;